(() => {
  const initializeBundleSelector = (root) => {
    if (!root || root.dataset.ppBundleReady === "true") return;
    root.dataset.ppBundleReady = "true";

    const options = Array.from(root.querySelectorAll("[data-bundle-card-purchase-option]"));
    const price = root.querySelector("[data-bundle-card-price]");
    const priceLabel = root.querySelector("[data-bundle-card-price-label]");
    const savings = root.querySelector("[data-bundle-card-savings]");
    const unitPrice = root.querySelector("[data-bundle-card-unit]");
    const details = root.querySelector("[data-bundle-card-details]");
    const buttonPrice = root.querySelector("[data-bundle-card-button-price]");
    const sellingPlanInput = root.querySelector("[data-pp-selling-plan-input]");

    const selectOption = (option) => {
      options.forEach((candidate) => {
        const label = candidate.closest("label");
        const selected = candidate === option;
        candidate.checked = selected;
        label?.classList.toggle("is-selected", selected);
      });

      if (price) price.textContent = option.dataset.purchasePrice || "";
      if (priceLabel) priceLabel.textContent = option.dataset.purchaseLabel || "";
      if (savings) savings.textContent = option.dataset.purchaseSavings || "";
      if (unitPrice) unitPrice.textContent = option.dataset.purchaseUnit || "";
      if (details) details.textContent = option.dataset.purchaseDetails || "";
      if (buttonPrice) buttonPrice.textContent = option.dataset.purchasePrice || "";

      if (sellingPlanInput) {
        const planId = option.dataset.sellingPlanId || "";
        sellingPlanInput.value = planId;
        sellingPlanInput.disabled = planId === "";
      }
    };

    options.forEach((option) => {
      option.addEventListener("change", () => selectOption(option));
    });

    const selected = options.find((option) => option.checked) || options[0];
    if (selected) selectOption(selected);
  };

  const initializeCarousel = (root) => {
    if (!root || root.dataset.ppCarouselReady === "true") return;
    root.dataset.ppCarouselReady = "true";

    const track = root.querySelector("[data-carousel-track]");
    const items = Array.from(root.querySelectorAll("[data-carousel-item]"));
    const previous = root.querySelector("[data-carousel-prev]");
    const next = root.querySelector("[data-carousel-next]");
    const dots = Array.from(root.querySelectorAll("[data-carousel-dot]"));
    const status = root.querySelector("[data-carousel-status]");
    const reducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)");
    let activeIndex = 0;
    let animationFrame = 0;

    if (!track || items.length === 0) return;

    const maxScroll = () => Math.max(0, track.scrollWidth - track.clientWidth);
    const itemOffset = (item) =>
      Math.max(0, Math.min(item.offsetLeft - track.offsetLeft, maxScroll()));

    const updateState = (index) => {
      activeIndex = Math.max(0, Math.min(items.length - 1, index));
      const overflow = maxScroll() > 1;
      root.classList.toggle("is-carousel-active", overflow);
      dots.forEach((dot, dotIndex) => {
        if (dotIndex === activeIndex) {
          dot.setAttribute("aria-current", "true");
        } else {
          dot.removeAttribute("aria-current");
        }
      });
      if (status) status.textContent = `${activeIndex + 1} of ${items.length}`;
      if (previous) previous.disabled = !overflow || activeIndex === 0;
      if (next) next.disabled = !overflow || activeIndex === items.length - 1;
    };

    const goTo = (index) => {
      const boundedIndex = Math.max(0, Math.min(items.length - 1, index));
      track.scrollTo({
        left: itemOffset(items[boundedIndex]),
        behavior: reducedMotion.matches ? "auto" : "smooth"
      });
      updateState(boundedIndex);
    };

    previous?.addEventListener("click", () => goTo(activeIndex - 1));
    next?.addEventListener("click", () => goTo(activeIndex + 1));
    dots.forEach((dot, index) => dot.addEventListener("click", () => goTo(index)));

    track.addEventListener("keydown", (event) => {
      if (event.key === "ArrowLeft") {
        event.preventDefault();
        goTo(activeIndex - 1);
      }
      if (event.key === "ArrowRight") {
        event.preventDefault();
        goTo(activeIndex + 1);
      }
      if (event.key === "Home") {
        event.preventDefault();
        goTo(0);
      }
      if (event.key === "End") {
        event.preventDefault();
        goTo(items.length - 1);
      }
    });

    track.addEventListener(
      "scroll",
      () => {
        window.cancelAnimationFrame(animationFrame);
        animationFrame = window.requestAnimationFrame(() => {
          const nearest = items.reduce(
            (best, item, index) => {
              const distance = Math.abs(track.scrollLeft - itemOffset(item));
              return distance < best.distance ? { distance, index } : best;
            },
            { distance: Number.POSITIVE_INFINITY, index: 0 }
          );
          updateState(nearest.index);
        });
      },
      { passive: true }
    );

    const synchronize = () => {
      const nearest = items.reduce(
        (best, item, index) => {
          const distance = Math.abs(track.scrollLeft - itemOffset(item));
          return distance < best.distance ? { distance, index } : best;
        },
        { distance: Number.POSITIVE_INFINITY, index: 0 }
      );
      updateState(nearest.index);
    };

    if ("ResizeObserver" in window) {
      new ResizeObserver(synchronize).observe(track);
    } else {
      window.addEventListener("resize", synchronize, { passive: true });
    }
    synchronize();
  };

  const initializeAccordion = (root) => {
    if (!root || root.dataset.ppAccordionReady === "true") return;
    root.dataset.ppAccordionReady = "true";
    const items = Array.from(root.querySelectorAll("details"));

    const synchronize = () => {
      items.forEach((item) => {
        item.querySelector("summary")?.setAttribute("aria-expanded", item.open ? "true" : "false");
      });
    };

    items.forEach((item) => {
      item.addEventListener("toggle", () => {
        if (item.open) {
          items.forEach((candidate) => {
            if (candidate !== item) candidate.open = false;
          });
        }
        synchronize();
      });
    });

    synchronize();
  };

  const initializeAll = (scope = document) => {
    if (scope.matches?.("[data-pp-bundle-selector]")) initializeBundleSelector(scope);
    if (scope.matches?.("[data-carousel]")) initializeCarousel(scope);
    if (scope.matches?.("[data-accordion-single]")) initializeAccordion(scope);
    scope.querySelectorAll("[data-pp-bundle-selector]").forEach(initializeBundleSelector);
    scope.querySelectorAll("[data-carousel]").forEach(initializeCarousel);
    scope.querySelectorAll("[data-accordion-single]").forEach(initializeAccordion);
  };

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", () => initializeAll(), { once: true });
  } else {
    initializeAll();
  }

  document.addEventListener("shopify:section:load", (event) => initializeAll(event.target));
})();
